/*
 *
 *  * Copyright (c) 2018-2019 AccelByte Inc. All Rights Reserved.
 *  * This is licensed software from AccelByte Inc, for limitations
 *  * and restrictions contact your company contract manager.
 *
 */

import { default as React, lazy } from "react";
import { Redirect, Route, RouteProps, Switch } from "react-router-dom";
import { Helmet } from "react-helmet";
// SB-259 Remove global Toast — Toast is rendered in Header.tsx instead
import { i18nInstance, t } from "./utils/i18n/i18n";
import history from "./utils/history";
import { HeaderHelmet } from "./components/HeaderHelmet/HeaderHelmet";
// SA-40 add Zendesk support form
import { zendeskKey } from "./const/env";
import { Footer } from "./components/Starbreeze/Footer/Footer"; // SA-1007 restyling language selector

const NotFoundPage = lazy(() => import("./components/error-pages/NotFoundPage/NotFoundPage"));
const InvalidRequestPage = lazy(() => import("./components/error-pages/InvalidRequestPage/InvalidRequestPage"));
const ServerIssuesPage = lazy(() => import("./components/error-pages/ServerIssuesPage/ServerIssuesPage"));
const LoginPage = lazy(() => import("./components/LoginPage/LoginPage"));
const RegisterPage = lazy(() => import("./components/RegisterPage/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./components/ForgotPasswordPage/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./components/ResetPasswordPage/ResetPasswordPage"));
const AlreadyAuthenticatedPage = lazy(() =>
  import("./components/error-pages/AlreadyAuthenticatedPage/AlreadyAuthenticatedPage")
);
const InvitationPage = lazy(() => import("./components/InvitationPage/InvitationPage"));

interface Props {}

interface State {
  initialized: boolean;
}

const validPathRegex = new RegExp("^\\/?([A-z0-9\\-_]+\\/)*([A-z0-9\\-_]+|)$");

// SA-40 add Zendesk support form: start
let launcherTimeOut: NodeJS.Timeout;
// SA-40 add Zendesk support form: end

class App extends React.Component<Props, State> {
  // SB-259 Remove global Toast — static contextType = ToastContext removed
  constructor(props: Props) {
    super(props);
    this.state = {
      initialized: false,
    };
  }

  async componentDidMount(): Promise<void> {
    await Promise.all([
      await import("./app-tasks")
        .then((module) => module.default)
        .then((taskRunner) => taskRunner()),
    ]).then(() => this.setState({ initialized: true }));
    i18nInstance.on("languageChanged", () => this.setState({}));

    this.redirectToTargetPath();
    // SA-40 add Zendesk support form: start
    if (zendeskKey !== "") {
      this.onIframeLoad();
    }
    // SA-40 add Zendesk support form: end
  }

  redirectToTargetPath = () => {
    const searchParam = new URLSearchParams(window.location.search);
    const targetPath = searchParam.get("target_auth_page");
    if (!targetPath) return;

    if (!validPathRegex.test(targetPath)) {
      history.replace("/error/400");
      return;
    }
    searchParam.delete("target_auth_page");
    history.replace(`${targetPath}?${searchParam.toString()}`);
  };

  // SA-40 add Zendesk support form: start
  isIFrame = (checkDom: HTMLElement | null): checkDom is HTMLIFrameElement =>
    checkDom !== null && checkDom.tagName === "IFRAME";

  onIframeLoad = () => {
    launcherTimeOut = setInterval(() => {
      const launcher = document.getElementById("launcher");

      if (launcher !== null) {
        if (this.isIFrame(launcher) && launcher.contentWindow) {
          if (launcher.contentWindow.document.getElementsByTagName("span")[1]) {
            launcher.contentWindow.document.getElementsByTagName("span")[1].innerText = t(
              "Starbreeze.zendesk.button.text"
            );
            clearTimeout(launcherTimeOut);
          }
        }
      }
    }, 100);
  };
  // SA-40 add Zendesk support form: end

  render() {
    // SA-40 add Zendesk support form: start
    const zendeskHelmet = zendeskKey !== "" && window.self === window.top && (
      <Helmet>
        <script
          type="text/javascript"
          crossOrigin="true"
          id="ze-snippet"
          src={`https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`}
        ></script>
      </Helmet>
    );
    // SA-40 add Zendesk support form: end

    return (
      <div className="App">
        {/* SA-325-Pop-up-modal-addition-onthe-sign-in-sign-up-page: start */}
        <HeaderHelmet />
        {zendeskHelmet}
        {/* SA-325-Pop-up-modal-addition-onthe-sign-in-sign-up-page: end */}
        {/* SB-259 Remove global Toast — Toast is rendered in Header.tsx instead */}
        <Switch>
          <Route exact={true} path="/error/404" component={NotFoundPage} />
          <Route exact={true} path="/error/400" component={InvalidRequestPage} />
          <Route exact={true} path="/error/500" component={ServerIssuesPage} />
          <Route exact={true} path="/error/already-authenticated" component={AlreadyAuthenticatedPage} />
          <Route exact={true} path="/invitation/:invitationId" component={InvitationPage} />
          <Route exact={true} path="/register" component={RegisterPage} />
          <Route exact={true} path="/forgot-password" component={ForgotPasswordPage} />
          <Route exact={true} path="/reset-password" component={ResetPasswordPage} />
          <Route exact={true} path="/login" component={LoginPage} />
          <Route
            exact={true}
            path="/"
            render={(props: RouteProps) => (
              <Redirect to={{ pathname: "/login", search: props && props.location && props.location.search }} />
            )}
          />
          <Redirect to="/error/404" />;
        </Switch>
        {/* // SA-1007 restyling language selector: start */}
        <Footer />
        {/* // SA-1007 restyling language selector: end */}
      </div>
    );
  }
}

export default App;
